/**
 * Entries surfaced in the Notifications panel.
 *
 * ## Adding an update
 *
 * Add an entry to the top of {@link UPDATES} in the same PR as the user-visible change, so the
 * update reaches users on the deploy that ships it. Entries are bundled with the app — there is no
 * backend — which keeps the copy reviewable alongside the code it describes.
 *
 * Write for someone who does not read our commits: what changed, and why they should care. Skip
 * internal refactors, dependency bumps, and fixes nobody noticed.
 *
 * **Only announce what is actually on in production.** A feature still behind a flag reaches a
 * fraction of users, so an entry about it puts a dot on everyone else's avatar pointing at
 * something they cannot see. Wait until the flag is on by default, then add the entry.
 *
 * ## Keeping the list short
 *
 * This is a "what changed lately" list, not an archive — there is no pagination, and every entry
 * ships inside the app bundle to every user.
 *
 * - **Drop entries older than roughly a month** when you add one. Prune in the same PR, so the
 *   list never becomes someone else's cleanup project.
 * - **The panel shows at most 5**, newest first. That cap lives in `useUpdates` and is a backstop,
 *   not a budget: if six entries are live, the sixth is invisible to everyone. Prune instead of
 *   relying on it.
 *
 * Before deleting an entry, nothing needs to be cleaned up elsewhere — ids are not referenced
 * outside this file. Do check that any `link` on a *remaining* entry still points somewhere real;
 * a route we have since renamed will quietly send people to the 404 page.
 */

export type UpdateLink = {
  label: string;
  /** In-app route (`/settings/api-keys`) or an absolute URL for docs and other external pages. */
  to: string;
  /** True for absolute URLs, so the link opens out of the app and gets the external affordance. */
  external?: boolean;
};

export type Update = {
  /**
   * Unique and stable. It is the React key and the value tests pin against, so never reuse or
   * rewrite one that has shipped. Convention: `yyyy-mm-dd-short-slug`.
   */
  id: string;
  /** Ship date as an ISO day, `yyyy-MM-dd`. Orders the list; unread is keyed on `id`. */
  date: string;
  title: string;
  /** One or two sentences in plain language. */
  summary: string;
  /** Optional supporting detail — keep each line short. */
  bullets?: string[];
  link?: UpdateLink;
};

/**
 * With no entries there is no dot anywhere, and the panel shows its empty state.
 *
 * Newest first. {@link useUpdates} sorts defensively, so an entry added out of order still renders
 * in the right place, but keeping the file ordered makes review easier.
 */
export const UPDATES: readonly Update[] = [];
